import React from 'react';
import { propEq } from 'ramda';
import { useRouter } from 'next/router';
import { useAsync } from 'react-use';

import { useBreakpoint } from '@hooks/useBreakpoint';
import { Artwork, IMAGE_URL_TYPE } from '@definitions/artworks.types';
import { HiddenContainer } from '@components/PLP/hiddenContainer';
import { ArtworkCard, ArtworkDetail } from '@components/artworkCard';
import { CUSTOM_FIELDS, LAYOUT_M_OPTIONS } from '@modules/plp/plp.constants';
import { useFilters } from '@hooks/useFilters';
import { useGroupings } from '@hooks/useHeader';
import { renderWhenOtherwise } from '@utils/rendering';
import { PRODUCT_TYPE, ROUTE } from '@utils/constants';
import { GiftCertificateCard } from '@components/giftCertificateCard';
import { hasEmptyFilters } from '@utils/helpers';

import { GridList, GridItem, GridItemContainer, SectionTitle } from './currentGrid.styles';

export interface CurrentGridProps {
  artworks: Artwork[];
  title: string;
}

export const CurrentGrid = ({ artworks, title }: CurrentGridProps) => {
  const { groupings, fetchGroupings } = useGroupings();
  const { isMobile } = useBreakpoint();
  const router = useRouter();
  const groupingSlug = router.query.grouping as string;
  const { filterOptions } = useFilters(groupingSlug);
  const isEmptyFilters = hasEmptyFilters(filterOptions || [], router.query);
  const imageHeight = isMobile ? 'calc(50vw - 30px)' : 'calc(33.333vw - 40px - 15px)';
  const defaultGrouping = groupings
    ? router.asPath.includes(`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${groupings[0]?.slug}`)
    : false;

  const getImageUrlType = (artwork: Artwork) => {
    if (artwork.layoutM === LAYOUT_M_OPTIONS.COLUMN_2 || (defaultGrouping && isEmptyFilters)) {
      return IMAGE_URL_TYPE.URL_STANDARD;
    }
    return IMAGE_URL_TYPE.URL_THUMBNAIL;
  };

  useAsync(async () => {
    await fetchGroupings();
  });

  const renderArtwork = renderWhenOtherwise(
    propEq('type', PRODUCT_TYPE.PRODUCT),
    (artwork) => (
      <GridItem>
        <ArtworkCard
          artwork={artwork}
          imageHeight={imageHeight}
          imageWidth="100%"
          imageUrlType={getImageUrlType(artwork as Artwork)}
          hasFavoriteOption={false}
        >
          <ArtworkDetail>
            {artwork.title}, {artwork.year}
          </ArtworkDetail>
          <ArtworkDetail>{artwork.medium}</ArtworkDetail>
        </ArtworkCard>
      </GridItem>
    ),
    (giftCertificate) => (
      <GridItem>
        <GiftCertificateCard imageHeight={imageHeight} imageWidth="100%" giftCertificate={giftCertificate} />
      </GridItem>
    )
  );

  const getLayout = (artwork: Artwork) => {
    if (isEmptyFilters) return artwork[CUSTOM_FIELDS.LAYOUT_M];
    return false;
  };

  return (
    <>
      <SectionTitle>{title}</SectionTitle>
      <GridList>
        {artworks.map((artwork, index) => (
          <GridItemContainer layoutM={getLayout(artwork)} key={index}>
            <HiddenContainer index={index} />
            {renderArtwork(artwork)}
          </GridItemContainer>
        ))}
      </GridList>
    </>
  );
};
